import { useState } from 'react';
import { useDebounce } from 'react-use';
import { useTranslation } from 'react-i18next';
import { KoobSearchBar } from '@koob/ui';
import { useSnack } from '../../../hooks';
import Spinner from '../../../components/Spinner';
import TripCard from '../../../components/Trip/TripCard';
import TripList, {
  buildTripRequestFilters,
} from '../../../components/Trip/TripList';

/**
 * Template chooser shown inside the design-trip wizard — same list as the
 * library pages, trimmed to a search box so the trip can be started without
 * leaving the modal.
 */
export default function TemplatePicker({ isDistributedTrip, onSelect }) {
  const { t } = useTranslation('tripDesigner');
  const { notify } = useSnack();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useDebounce(() => setDebouncedSearch(search), 500, [search]);

  // Loading the picked template takes a couple of round trips — freeze the
  // list rather than leave the click looking ignored.
  if (loading) {
    return <Spinner noAbsolute />;
  }

  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="w-full max-w-md">
        <KoobSearchBar
          placeholder={t('misc:searchByNameOrId')}
          value={search}
          onChange={setSearch}
        />
      </div>

      <div className="mt-4">
        <TripList
          columns="md:grid-cols-2 xl:grid-cols-3"
          filters={buildTripRequestFilters(
            { search: debouncedSearch },
            { type: 'template', isDistributedTrip },
          )}
          renderCount={count => (
            <h6 className="mb-3 text-gray-500">
              {t('library.results', { count })}
            </h6>
          )}
          renderCard={trip => (
            <TripCard
              trip={trip}
              onSelect={() => {
                setLoading(true);
                Promise.resolve(onSelect(trip))
                  .catch(() =>
                    notify(t('library.template.loadError'), { type: 'error' }),
                  )
                  .finally(() => setLoading(false));
              }}
            />
          )}
        />
      </div>
    </div>
  );
}
